import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Key, Lock } from "@phosphor-icons/react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { useVerifiedFactor } from "../auth/stepUp";
import { useStepUpProof } from "../auth/useStepUpProof";
import { useTenantRoster } from "../data/tenantRoster";
import {
  secretFailureText,
  useCancelSecret,
  useSecretRecipients,
  useSecretTransfers,
  useSendSecret,
  type SecretRecipient,
  type SecretStatus,
  type SecretTransfer
} from "../data/secretQueries";
import { Quiet } from "../ui/Badge";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { ClientHead } from "../ui/ClientHead";
import { Hint } from "../ui/Hint";
import { Panel } from "../ui/Panel";
import { RowItem } from "../ui/RowLinks";
import { Segmented } from "../ui/Segmented";
import { Sheet } from "../ui/Sheet";
import "./clientSettings/settings.css";

/**
 * `Skicka en hemlighet`, plan.md section 5.10.
 *
 * The Worker seals and opens, the database decides tenancy, the recipient's verified MFA, the step-up
 * grant and the single conditional view, and `app/routes/ChatRoute.tsx` points here from the
 * composer at the moment somebody is about to type a password into a message.
 *
 * ---
 *
 * **Where it lives and why it is not an eighth destination.** `design.md` section 5 fixes seven
 * client navigation destinations and `tests/portal-interface.test.tsx` guards the count. This route
 * sits inside the client shell and outside the navigation, exactly as `/notifications` does, and is
 * reached from the chat composer, from `Inställningar` and from the notification that says a secret
 * is waiting.
 *
 * **Petrovic is a recipient, and that is the case this screen exists for.** plan.md 5.10 tells the
 * recipient to "rotate a shared password or key when practical", which is a client handing its
 * supplier a login. Until 2026-09-13 the gateway allowed only colleagues at the same client, so a
 * client who needed to send Petrovic a password had mail and nothing else. The picker now offers
 * Petrovic first.
 *
 * **The sender's own factor is not required.** plan.md 5.10 puts that requirement on the recipient,
 * for a reason about the value being read. The sender re-authenticates through
 * `app/auth/useStepUpProof.tsx`: the code from the verifieringsapp when the account has one, the
 * current password when it does not.
 *
 * **The plaintext lives in the textarea and nowhere else.** It is an uncontrolled field read through
 * a ref at the moment of submission, so it is never React state, never a mutation variable and never
 * anything TanStack Query retains. `app/data/secretQueries.ts` carries the whole argument.
 *
 * **Hemligheter, one list and a sheet, since the second build round of 2026-09-24**
 * (`prototype/kund/pages.js`, `secretsPage`). The page was headed by its form; it is headed by what it
 * holds now, with sending as the page's one act in a sheet and the two lists behind "Till er" and
 * "Skickade". The intro and the two leads went under design.md section 3's third rule, and what they
 * said that matters is the one line behind the question mark. Hemligheter is also reached from the
 * company's group in Inställningar and from the Mer sheet since that round.
 */

const dateTime = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long", timeStyle: "short" });

/**
 * The four outcomes, in Swedish, plus the derived one.
 *
 * `expired` is never written to the column by anything: the read model derives it from the row's own
 * expiry, because the retention sweep deletes an aged-out transfer rather than marking it.
 */
const STATUS_LABEL: Record<SecretStatus, string> = {
  waiting: "Väntar",
  viewed: "Öppnad",
  expired: "Utgången",
  cancelled: "Återkallad"
};

/**
 * The name for a counterparty nobody in this client's roster has.
 *
 * `public.create_one_time_secret` admits exactly two kinds of counterparty: an active member of the
 * client's own tenant, who is in `useTenantRoster` whatever their status, and a Petrovic admin, who
 * never can be. So a transfer whose other side is not in the roster is a transfer with Petrovic. A
 * membership could only leave the roster by being deleted, which retention does twelve months after
 * the relationship ends, and a transfer lives seven days at most.
 */
const PETROVIC = "Petrovic Systems";

function formatMoment(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : dateTime.format(parsed);
}

interface RowProps {
  transfer: SecretTransfer;
  counterparty: string;
  onCancel?: (id: string) => void;
  cancelling: boolean;
}

/**
 * One transfer as one row: what it is about, who is on the other side and when, and at the end
 * either what can be done with it or the state it is in (the sketch's `secretsPage`).
 *
 * A waiting transfer shows its act, Öppna or Återkalla, where the others show their state as a grey
 * word. The word "Väntar" is still in the row for a screen reader, because the act alone implies the
 * state to an eye and not to an ear.
 */
function TransferRow({ transfer, counterparty, onCancel, cancelling }: RowProps) {
  const waiting = transfer.status === "waiting";
  const when =
    transfer.status === "viewed"
      ? formatMoment(transfer.viewed_at)
      : transfer.status === "waiting"
        ? (() => {
            const until = formatMoment(transfer.expires_at);
            return until ? `gäller till ${until}` : null;
          })()
        : transfer.status === "expired"
          ? formatMoment(transfer.expires_at)
          : null;

  const who = transfer.direction === "sent" ? `Till ${counterparty}` : `Från ${counterparty}`;

  let trail: ReactNode;
  if (transfer.direction === "received" && waiting) {
    trail = (
      <>
        <span className="visually-hidden" data-testid="secret-status">
          {STATUS_LABEL[transfer.status]}
        </span>
        <Link className="button" to={`/secrets/${transfer.id}`}>
          Öppna
        </Link>
      </>
    );
  } else if (transfer.direction === "sent" && waiting && onCancel) {
    trail = (
      <>
        <span className="visually-hidden" data-testid="secret-status">
          {STATUS_LABEL[transfer.status]}
        </span>
        <button
          type="button"
          className="button is-quiet"
          disabled={cancelling}
          data-testid="secret-cancel"
          onClick={() => onCancel(transfer.id)}
        >
          {cancelling ? "Återkallar…" : "Återkalla"}
        </button>
      </>
    );
  } else {
    // Shape and word, never colour alone. design.md section 5 fixes that rule and it holds anywhere
    // a state is reported; here the word is the whole of it, because none of these asks anything.
    trail = <Quiet testId="secret-status">{STATUS_LABEL[transfer.status]}</Quiet>;
  }

  return (
    <RowItem testId="secret-row" trail={trail}>
      <span className="primary-cell">
        <strong>{transfer.purpose_label}</strong>
        <span>{when ? `${who} · ${when}` : who}</span>
      </span>
    </RowItem>
  );
}

type Part = "received" | "sent";

const PARTS = [
  { value: "received", label: "Till er" },
  { value: "sent", label: "Skickade" }
] as const;

export function SecretsRoute() {
  const identity = usePortalIdentity();
  const factor = useVerifiedFactor();
  const tenantId = identity.data?.tenantId ?? null;
  const userId = identity.data?.userId ?? null;
  const writable = identity.data?.portalAccessState === "write";

  const transfers = useSecretTransfers(Boolean(tenantId));
  // `public.secret_recipients` refuses a tenant the caller cannot write to, and a read-only client
  // cannot send anyway, so it is not asked.
  const recipients = useSecretRecipients(tenantId, writable);
  const roster = useTenantRoster(tenantId);
  const cancel = useCancelSecret();

  const [part, setPart] = useState<Part>("received");
  const [sending, setSending] = useState(false);
  /*
   * The withdrawal's refusal has its own state and its own place on the page, because the form it
   * would otherwise share is in a sheet that is not always open. A paused förvaltning takes the form
   * away entirely, and that person may still have a waiting transfer to withdraw.
   */
  const [cancelFailure, setCancelFailure] = useState<string | null>(null);

  const head = (actions?: ReactNode) => (
    <ClientHead title="Hemligheter" titleId="secrets-title" actions={actions} />
  );

  if (identity.isLoading) {
    return (
      <div className="k-page k-secrets">
        {head()}
        <Panel flush>
          <BoxState kind="loading" title="Hämtar ditt konto" />
        </Panel>
      </div>
    );
  }

  if (identity.isError || !tenantId || !userId) {
    return (
      <div className="k-page k-secrets">
        {head()}
        <Panel flush>
          <BoxState
            kind="denied"
            title="Ingen aktiv åtkomst"
            body="Vi kan inte se vilket kundkonto du hör till, så det går inte att skicka något härifrån. Ladda om sidan, och skriv i chatten om det står kvar."
          />
        </Panel>
      </div>
    );
  }

  const names = roster.data ?? new Map<string, string>();
  const rows = (transfers.data ?? []).filter((row) => row.direction === part);

  function list() {
    if (transfers.isLoading) {
      return <BoxState kind="loading" title="Hämtar" compact />;
    }

    if (transfers.isError) {
      return (
        <BoxState
          kind="error"
          title="Listan gick inte att hämta"
          body="Ingenting har försvunnit. Försök igen, och skriv i chatten om det upprepas."
          onRetry={() => void transfers.refetch()}
        />
      );
    }

    if (rows.length === 0) {
      return (
        <BoxState
          kind="empty"
          compact
          title={part === "sent" ? "Ni har inte skickat någon hemlighet." : "Ingen hemlighet väntar på er."}
        />
      );
    }

    return (
      <ul className="k-rows" data-testid={`secret-list-${part}`}>
        {rows.map((row) => (
          <TransferRow
            key={row.id}
            transfer={row}
            counterparty={
              row.counterparty_user_id ? (names.get(row.counterparty_user_id) ?? PETROVIC) : PETROVIC
            }
            cancelling={cancel.isPending}
            onCancel={
              part === "sent"
                ? (id) => {
                    setCancelFailure(null);
                    cancel.mutate(
                      { id },
                      { onError: (error) => setCancelFailure(secretFailureText(error)) }
                    );
                  }
                : undefined
            }
          />
        ))}
      </ul>
    );
  }

  return (
    <div className="k-page k-secrets">
      {head(
        writable ? (
          <button
            type="button"
            className="button is-primary"
            data-testid="secret-open"
            onClick={() => setSending(true)}
          >
            <Key size={16} aria-hidden="true" />
            <span>Skicka en hemlighet</span>
          </button>
        ) : (
          <span className="k-locked">
            <Lock size={16} aria-hidden="true" /> Läsläge
          </span>
        )
      )}

      {writable ? null : (
        <Callout tone="warn" testId="secret-read-only">
          Förvaltningen är pausad, så det går inte att skicka en hemlighet just nu. Ni kan fortfarande
          öppna en som är skickad till er.
        </Callout>
      )}

      <div className="toolbar k-secret-toolbar">
        <Segmented<Part>
          label="Vilka hemligheter"
          value={part}
          options={PARTS}
          onChange={(next) => setPart(next)}
          testId="secret-part"
        />
        <Hint>För ett lösenord som ska byta händer en gång. Det går att öppna en gång och raderas sedan.</Hint>
      </div>

      <Panel flush testId="secret-list-panel">
        {cancelFailure ? (
          <p className="form-error k-set-inset" role="alert" data-testid="secret-cancel-error">
            {cancelFailure}
          </p>
        ) : null}
        {list()}
      </Panel>

      {writable ? (
        <Sheet open={sending} onOpenChange={setSending} title="Skicka en hemlighet" testId="secret-sheet">
          <SendSecretForm
            tenantId={tenantId}
            factor={factor}
            recipients={recipients.data ?? []}
            recipientsFailed={recipients.isError}
          />
        </Sheet>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ sending */

/**
 * The send form, in the sheet (the sketch's `skicka`). The sheet renders nothing while closed, so the
 * textarea that holds the plaintext exists only while the sheet is open, and closing the sheet drops
 * the node and whatever was typed into it.
 */
function SendSecretForm({
  tenantId,
  factor,
  recipients,
  recipientsFailed
}: {
  tenantId: string;
  factor: ReturnType<typeof useVerifiedFactor>;
  recipients: SecretRecipient[];
  recipientsFailed: boolean;
}) {
  const send = useSendSecret();

  const [proof, proofFields] = useStepUpProof({
    action: "create_one_time_secret",
    factor: {
      id: factor.data ?? null,
      unknown: factor.isError,
      pending: factor.data === undefined && !factor.isError
    },
    disabled: send.isPending,
    idPrefix: "secret",
    totpReason:
      "Vi behöver veta att det är du innan värdet lämnar din dator. Koden är det som styrker det, och den gäller i fem minuter.",
    passwordReason:
      "Vi behöver veta att det är du innan värdet lämnar din dator. Kontot har ingen verifieringsapp, så lösenordet är det som styrker det. Du loggas inte ut och lösenordet ändras inte."
  });

  const valueField = useRef<HTMLTextAreaElement>(null);
  const [recipientUserId, setRecipientUserId] = useState("");
  const [purposeLabel, setPurposeLabel] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const ready =
    recipientUserId.length > 0 && purposeLabel.trim().length > 0 && proof.ready && !send.isPending;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    setSent(null);

    /*
     * The node, read once, and emptied whatever happens next.
     *
     * `readValue` is called inside the mutation, so the string is passed to `fetch` and is never
     * held by anything with a lifetime. The `finally` clears the field on success and on failure
     * alike: a value still sitting in a textarea after a refused send is a copy the person did not
     * ask for and would not think to remove.
     */
    const node = valueField.current;
    const value = node?.value ?? "";

    if (value.length === 0) {
      setFailure("Skriv hemligheten innan du skickar.");
      return;
    }

    try {
      await send.mutateAsync({
        tenantId,
        recipientUserId,
        purposeLabel: purposeLabel.trim(),
        requestGrant: proof.request,
        readValue: () => value
      });

      setSent(purposeLabel.trim());
      setPurposeLabel("");
      setRecipientUserId("");
    } catch (error) {
      setFailure(secretFailureText(error));
    } finally {
      if (node) {
        node.value = "";
      }
      // A TOTP code is single use and a Turnstile token is single use, so neither survives a send.
      proof.reset();
    }
  }

  return (
    <form className="k-form" onSubmit={submit} data-testid="secret-form">
      {failure ? (
        <p className="form-error" role="alert" data-testid="secret-send-error">
          {failure}
        </p>
      ) : null}

      {sent ? (
        <p className="form-success" role="status" data-testid="secret-sent">
          Hemligheten om {sent} är skickad. Mottagaren får en notis och kan öppna den en gång. Ni kan
          inte se värdet igen.
        </p>
      ) : null}

      <div className="field">
        <label htmlFor="secret-recipient">Vem ska få den</label>
        <select
          id="secret-recipient"
          value={recipientUserId}
          disabled={send.isPending}
          onChange={(event) => setRecipientUserId(event.target.value)}
        >
          <option value="">Välj mottagare</option>
          {recipients.map((person) => (
            <option key={person.userId} value={person.userId}>
              {person.kind === "petrovic" ? PETROVIC : person.name}
            </option>
          ))}
        </select>
        <p className="settings-note">
          Petrovic Systems eller någon i er egen organisation. Mottagaren måste ha tvåfaktorsinloggning
          påslagen, annars säger portalen ifrån när ni skickar.
        </p>
      </div>

      {recipientsFailed ? (
        <p className="form-error" role="alert" data-testid="secret-recipients-error">
          Vi kunde inte hämta vilka ni kan skicka till. Ladda om sidan och försök igen.
        </p>
      ) : null}

      <div className="field">
        <label htmlFor="secret-purpose">Vad gäller den</label>
        <input
          id="secret-purpose"
          maxLength={200}
          autoComplete="off"
          value={purposeLabel}
          disabled={send.isPending}
          onChange={(event) => setPurposeLabel(event.target.value)}
        />
        <p className="settings-note">
          Det här syns för mottagaren och i er egen lista. Skriv vad det gäller, aldrig värdet.
        </p>
      </div>

      <div className="field">
        <label htmlFor="secret-value">Hemligheten</label>
        {/*
          Uncontrolled on purpose. See the file header: the plaintext lives in this node and is read
          at submission, so it never becomes React state, a mutation variable or a cache entry.
          `autoComplete` off and `spellCheck` false keep the browser from offering to remember it or
          sending it away to be checked.
        */}
        <textarea
          id="secret-value"
          ref={valueField}
          rows={3}
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={send.isPending}
          data-testid="secret-value"
          className="k-secret-input"
        />
        <p className="settings-note">Lösenord, API-nyckel eller motsvarande. Fält för filer finns inte här.</p>
      </div>

      {proofFields}

      <div className="form-actions">
        <button type="submit" className="button is-primary" disabled={!ready} data-testid="secret-submit">
          {send.isPending ? "Skickar…" : "Skicka hemligheten"}
        </button>
      </div>
    </form>
  );
}
